import { useCallback } from 'react';

import { usePublicView } from './publicView';

/**
 * Clicking a status pill this link didn't grant used to be a silent no-op
 * (`PUBLIC_NOOP`) — now it prompts sign-in with the click captured as a
 * resumable `select_pill` pending action. Was hand-duplicated ~20 lines at a
 * time across CreatorsTab/ConfirmedTab/ScriptsTab/ContentTab/LiveContentTab —
 * one shared hook instead, so the next tab that needs this doesn't become a
 * sixth copy and a five-way-audited `tab` literal doesn't silently drift out
 * of sync with the other four.
 *
 * `tab` is the pending-action tab identifier for THIS tab's own screen
 * (e.g. `'creators'`, `'confirmed'`) — fixed per caller, not per click.
 * `activePill` is the pill this link already granted; clicking it stays a
 * no-op since the visitor is already looking at it.
 */
export function usePublicPillCapture<Pill extends string>(
  tab: string,
  activePill: Pill,
): (pill: Pill) => void {
  const { campCode, tail, requestLogin } = usePublicView();
  return useCallback(
    (pill: Pill) => {
      if (pill === activePill) return;
      if (campCode && tail) {
        requestLogin({
          type: 'select_pill',
          campCode,
          tail,
          tab,
          pill,
          createdAt: Date.now(),
        });
      } else {
        requestLogin();
      }
    },
    [tab, activePill, campCode, tail, requestLogin],
  );
}

/**
 * "View analysis" resume — mirrors usePublicPillCapture's rationale, for the
 * `open_insights` pending-action type instead of `select_pill`. Row shapes
 * differ across callers (CreatorsRow, RosterEntryFinalized, ...), so this
 * takes the already-resolved username rather than a row, keeping the hook
 * itself row-type-agnostic.
 */
export function usePublicInsightsCapture(tab: string): (entryUsername: string | undefined) => void {
  const { campCode, tail, requestLogin } = usePublicView();
  return useCallback(
    (entryUsername: string | undefined) => {
      if (entryUsername && campCode && tail) {
        requestLogin({
          type: 'open_insights',
          campCode,
          tail,
          tab,
          entryUsername,
          createdAt: Date.now(),
        });
      } else {
        requestLogin();
      }
    },
    [tab, campCode, tail, requestLogin],
  );
}
